// Purpose: searchable single-select with a free-text fallback. Used by
// admin/Forensics.tsx to pick a break-glass target by human label
// (decrypted server-side) without the operator having to paste a UUID.
// Free-text mode kicks in when an operator needs to inspect an
// archived/deleted target whose ID isn't in the active list.
//
// Click-outside + Escape close the dropdown. CSP-clean (no inline
// styles, no innerHTML).
//
// Commit 8b.12.1: popover renders through createPortal into document.body
// so it escapes the form's stacking context. Position + width come from
// usePopoverPosition; written to the element via CSS custom properties
// (same posture as ProgressFill — keeps the prod CSP style-src 'self'
// clean).

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, SearchIcon } from '../icons/index.tsx';
import { usePopoverPosition } from '../../lib/popover-position.ts';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional hint rendered after the label (e.g. created date, status). */
  hint?: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Empty-state copy shown when no options match the search query. */
  emptyText?: string;
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string;
  /** Disable the picker (e.g. while options are loading). */
  disabled?: boolean;
  /** Loading state for the option list. */
  loading?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  emptyText = 'No matches.',
  placeholder = 'Choose…',
  disabled,
  loading,
  className,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const position = usePopoverPosition(triggerRef, open);

  useEffect(() => {
    if (!open) return;
    const onMouse = (ev: MouseEvent): void => {
      const target = ev.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // CSS-var positioning so the rendered DOM doesn't carry an inline
  // style="" attribute. Width is pinned to the trigger's width so the
  // popover stays the same shape as the closed state.
  useEffect(() => {
    const el = popoverRef.current;
    if (!el || !position) return;
    el.style.setProperty('--popover-top', `${position.top}px`);
    el.style.setProperty('--popover-left', `${position.left}px`);
    el.style.setProperty('--popover-width', `${position.width}px`);
  }, [position]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.hint ?? '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const selectedLabel = options.find((o) => o.value === value)?.label;

  // Manual mode: an inline text input replaces the dropdown trigger.
  // Useful when the operator needs to inspect a target that's outside
  // the active+recent list (deleted, archived, or anything older than
  // the 30-day window the API serves).
  if (manualMode) {
    return (
      <div className={['flex flex-col gap-1', className ?? ''].join(' ')}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste a target ID"
          className={[
            'h-10 px-3',
            'bg-bg-secondary border border-border-default rounded-md',
            'text-body-m text-fg-primary',
            'focus:border-accent-primary focus:outline-none',
          ].join(' ')}
          aria-label={ariaLabel}
        />
        <button
          type="button"
          onClick={() => {
            setManualMode(false);
            onChange('');
          }}
          className="self-start text-body-s text-accent-primary hover:underline"
        >
          ← Pick from list instead
        </button>
      </div>
    );
  }

  return (
    <div className={[className ?? ''].join(' ')}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={[
          'flex items-center justify-between gap-2 w-full h-10 px-3',
          'bg-bg-secondary border border-border-default rounded-md',
          'text-body-m text-fg-primary',
          'hover:border-border-strong',
          'focus:border-accent-primary focus:outline-none',
          'transition-colors duration-fast ease-out',
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
        ].join(' ')}
      >
        <span className={selectedLabel ? '' : 'text-fg-muted'}>{selectedLabel ?? placeholder}</span>
        <ChevronDownIcon size={16} className="text-fg-muted" aria-hidden />
      </button>

      {open && position && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              role="listbox"
              className={[
                'popover-portal popover-portal-trigger-width mt-2 z-50',
                'max-h-[420px] overflow-hidden flex flex-col',
                'bg-bg-elevated border border-border-default rounded-md shadow-lg',
              ].join(' ')}
            >
              <div className="relative p-2 border-b border-border-subtle">
                <SearchIcon
                  size={14}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"
                  aria-hidden
                />
                <input
                  type="search"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className={[
                    'w-full h-9 pl-8 pr-3',
                    'bg-bg-secondary border border-border-default rounded-sm',
                    'text-body-s text-fg-primary placeholder:text-fg-muted',
                    'focus:border-accent-primary focus:outline-none',
                  ].join(' ')}
                />
              </div>
              <div className="overflow-y-auto flex-1">
                {loading ? (
                  <div className="px-3 py-4 text-body-s text-fg-muted">Loading…</div>
                ) : filtered.length === 0 ? (
                  <div className="px-3 py-4 text-body-s text-fg-muted">{emptyText}</div>
                ) : (
                  filtered.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        onChange(opt.value);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={[
                        'w-full text-left flex items-center justify-between gap-2 px-3 py-2',
                        'text-body-s text-fg-primary hover:bg-bg-tertiary',
                        opt.value === value ? 'bg-bg-tertiary' : '',
                      ].join(' ')}
                    >
                      <span className="truncate">{opt.label}</span>
                      {opt.hint ? (
                        <span className="text-fg-muted text-caption shrink-0">{opt.hint}</span>
                      ) : null}
                    </button>
                  ))
                )}
              </div>
              <div className="border-t border-border-subtle p-2">
                <button
                  type="button"
                  onClick={() => {
                    setManualMode(true);
                    setOpen(false);
                  }}
                  className="text-body-s text-accent-primary hover:underline"
                >
                  Or enter ID manually →
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
